import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface OrgUserTeam {
  id: string;
  name: string;
  divisionName?: string | null;
}

/** One member of the organization as returned by GET /api/users. */
export interface OrgUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isAdmin: boolean;
  /** Owns the organization: answerable for it, and a super-administrator in it. */
  isOwner: boolean;
  createdAt: string;
  /** Null for an account that has never verified its email. */
  emailVerifiedAt?: string | null;
  teams: OrgUserTeam[];
}

interface UsersListResponse {
  users: OrgUser[];
}

const backendApi = config.backendApi;

const fetchUsers = async (): Promise<OrgUser[]> => {
  const response = await axios.get<UsersListResponse>(
    `${backendApi}/api/users`,
    { withCredentials: true }
  );
  return response.data.users;
};

export function useUsers(organizationId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<OrgUser[]>({
    // 'org-users' rather than 'users': the response gained team memberships,
    // so the key changed with the shape.
    queryKey: ['org-users', organizationId],
    queryFn: () => {
      if (!organizationId) {
        throw new Error("organizationId is required to fetch users.");
      }
      return fetchUsers();
    },
    enabled: !!organizationId && contextReady,
  });
}
